/**
 * Haber kategorileri ve URL slug ↔ API anahtarı eşlemesi.
 *
 * Bu modül BİLEREK 'use client' TAŞIMAZ: hem haber listesi sunucu bileşeni (SSR
 * tohumlaması için istenen kategoriyi çözerken) hem de NewsListClient kullanır.
 * Eşleme daha önce NewsListClient içinde tanımlıydı; sunucudan çağrılınca
 * "Attempted to call urlSlugToApiKey() from the server but it is on the client"
 * hatası fırlıyor, hata yutulduğu için liste sessizce boş kalıyordu.
 */

export type NewsCategory = {
  /** API'nin beklediği anahtar (Türkçe slug ile aynı). */
  key: string
  /** İngilizce URL slug'ı. */
  enKey: string
  /** Çeviri anahtarı. */
  i18n: string
  /** Rozet rengi (varsa). */
  color?: string
}

export const categoryKeys: NewsCategory[] = [
  { key: '', enKey: '', i18n: 'news.all' },
  { key: 'piyasalar', enKey: 'markets', i18n: 'news.markets', color: '#3498DB' },
  { key: 'ekonomi', enKey: 'economy', i18n: 'news.economy', color: '#27AE60' },
  { key: 'bilancolar', enKey: 'balance-sheets', i18n: 'news.earnings', color: '#8E44AD' },
  { key: 'dunya', enKey: 'world', i18n: 'news.world', color: '#E67E22' },
  { key: 'enerji', enKey: 'energy', i18n: 'news.energy', color: '#E74C3C' },
  { key: 'teknoloji', enKey: 'technology', i18n: 'news.tech', color: '#2980B9' },
  { key: 'kripto', enKey: 'crypto', i18n: 'news.crypto', color: '#F39C12' },
  { key: 'forex-emtia', enKey: 'forex-commodities', i18n: 'news.forexCommodity', color: '#1ABC9C' },
  { key: 'rehber', enKey: 'guide', i18n: 'news.guide', color: '#9B59B6' },
]

export const categoryColorMap: Record<string, string> = Object.fromEntries(
  categoryKeys.filter(c => c.color).map(c => [c.key, c.color!]),
)

/** URL slug'ını (TR veya EN) API kategori anahtarına çevirir. */
export function urlSlugToApiKey(slug: string): string {
  if (!slug) return ''
  const byTr = categoryKeys.find(c => c.key === slug)
  if (byTr) return byTr.key
  const byEn = categoryKeys.find(c => c.enKey === slug)
  if (byEn) return byEn.key
  return slug
}

/** API kategori anahtarını, sayfanın diline uygun URL slug'ına çevirir. */
export function apiKeyToUrlSlug(key: string, lang: string): string {
  if (!key) return ''
  if (lang === 'en') {
    const cat = categoryKeys.find(c => c.key === key)
    return cat?.enKey ?? key
  }
  return key
}

/** Boş ("Tümü") dışındaki gerçek kategoriler — kendi URL'i olanlar. */
export const realCategories: NewsCategory[] = categoryKeys.filter(c => c.key !== '')

/**
 * URL slug'ından kategori kaydını bulur. TR ve EN slug'ının ikisini de tanır;
 * tanımadığı slug için null döner (sayfa 404 vermeli, uydurma kategori sayfası
 * üretilmemeli — aksi halde sonsuz ince içerik yolu açılır).
 */
export function findCategoryBySlug(slug: string): NewsCategory | null {
  if (!slug) return null
  const decoded = decodeURIComponent(slug).toLowerCase()
  return realCategories.find(c => c.key === decoded || c.enKey === decoded) ?? null
}

/** Kategorinin verilen dildeki KANONİK URL slug'ı (tr → key, en → enKey). */
export function categorySlugFor(cat: NewsCategory, lang: string): string {
  return lang === 'en' ? cat.enKey : cat.key
}

/** Kategori liste sayfasının verilen dildeki tam yolu. */
export function categoryPath(cat: NewsCategory, lang: string): string {
  return lang === 'en'
    ? `/en/news/category/${cat.enKey}`
    : `/tr/haberler/kategori/${cat.key}`
}
